import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFieldDefinition, deleteFieldDefinition, updateFieldDefinition, listFieldDefinitions } from '@/api/assetFieldDefinitions';
import { createAccountType, deleteAccountType, listAccountTypes, updateAccountType } from '@/api/accountTypes';
import { getAudit } from '@/api/audit';
import { runSoftwareProvisionSweep, syncAccounts, syncArc, syncCrowdStrike, syncDefender, syncDellWarranty, syncIntune, syncSystemAccounts } from '@/api/sync';
import { runOneDriveMigrationCopy } from '@/api/onedriveMigration';
import { ApiError } from '@/api/client';
import { assetCategories } from '@/pages/Assets/schema';
import { formatDateTime } from '@/lib/dateFormat';
import type { AccountType, AccountTypeKind, AssetFieldDefinition, FieldDataType } from '@/types/oscar';

const inputCls = 'rounded border border-border bg-white px-2 py-1.5 text-sm';
const fieldDataTypes: FieldDataType[] = ['Text', 'Number', 'Date', 'Boolean', 'Select'];
const accountKinds: AccountTypeKind[] = ['Person', 'System'];

export function AdminPage() {
  return (
    <section className="max-w-4xl space-y-8">
      <header>
        <h1 className="font-heading text-2xl font-bold text-navy">Admin</h1>
        <p className="mt-1 text-sm text-darkGrey/80">Account types, asset field definitions, configuration and sync job status. Locations are managed on the Locations page.</p>
      </header>
      <AccountTypes />
      <AssetFieldDefinitions />
      <SyncStatus />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <InfoCard title="Asset tag format" body="ORG-XXXX — three-letter prefix, hyphen, four digits (e.g. ORG-0001). Validated on the asset form and by a SQL CHECK constraint." />
        <InfoCard title="Contract expiry look-ahead" body="Contracts within 90 days of their end date surface as ExpiringSoon. Configured via OSCAR_CONTRACT_LOOKAHEAD_DAYS (default 90)." />
      </div>
    </section>
  );
}

/**
 * Account Type config (migration 051). The categorisation for the unified
 * People / Accounts view. Kind (Person | System) routes the one-click "add"
 * flow to the right table. Code + Kind are immutable once created (they are the
 * routing contract); Name, Sort and Active are editable.
 */
function AccountTypes() {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<AccountTypeKind>('System');
  const [sortOrder, setSortOrder] = useState('');

  const query = useQuery({ queryKey: ['account-types'], queryFn: ({ signal }) => listAccountTypes(false, signal) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['account-types'] });

  const mCreate = useMutation({
    mutationFn: () => createAccountType({
      Code: code.trim(),
      Name: name.trim(),
      Kind: kind,
      SortOrder: sortOrder.trim() ? Number(sortOrder) : undefined,
    }),
    onSuccess: () => { invalidate(); setCode(''); setName(''); setKind('System'); setSortOrder(''); },
  });

  return (
    <div>
      <h2 className="mb-2 font-heading text-base font-semibold text-navy">Account types</h2>
      <p className="mb-2 text-xs text-darkGrey/70">Categories for the People / Accounts view. <span className="font-semibold">Kind</span> decides where a one-click add lands — Person accounts in People, System accounts (service, mailbox, …) in the system-account store.</p>
      <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr><th className="px-3 py-2">Code</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Kind</th><th className="px-3 py-2">Sort</th><th className="px-3 py-2">Active</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {query.data?.map((t) => <AccountTypeRow key={t.AccountTypeId} type={t} onChanged={invalidate} />)}
            {query.data?.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-darkGrey/60">No account types yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <input aria-label="Account type code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code (e.g. Contractor)" className={inputCls} />
        <input aria-label="Account type name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className={inputCls} />
        <select aria-label="Account type kind" value={kind} onChange={(e) => setKind(e.target.value as AccountTypeKind)} className={inputCls}>
          {accountKinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <input aria-label="Account type sort order" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} placeholder="Sort" type="number" className={`${inputCls} w-20`} />
        <button type="button" disabled={!code.trim() || !name.trim() || mCreate.isPending} onClick={() => mCreate.mutate()} className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40">Add type</button>
      </div>
      {mCreate.isError && <p role="alert" className="mt-2 text-xs text-red">Could not add the account type — the code may already exist.</p>}
    </div>
  );
}

function AccountTypeRow({ type, onChanged }: { type: AccountType; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(type.Name);
  const [sortOrder, setSortOrder] = useState(String(type.SortOrder));
  const [isActive, setIsActive] = useState(type.IsActive);

  const startEdit = () => {
    setName(type.Name);
    setSortOrder(String(type.SortOrder));
    setIsActive(type.IsActive);
    setEditing(true);
  };

  const mUpdate = useMutation({
    mutationFn: () => updateAccountType(type.AccountTypeId, {
      Name: name.trim(),
      SortOrder: sortOrder.trim() ? Number(sortOrder) : undefined,
      IsActive: isActive,
    }),
    onSuccess: () => { setEditing(false); onChanged(); },
  });
  const mDelete = useMutation({ mutationFn: () => deleteAccountType(type.AccountTypeId), onSuccess: onChanged });

  if (!editing) {
    return (
      <tr className="border-t border-border">
        <td className="px-3 py-2 font-mono text-xs">{type.Code}</td>
        <td className="px-3 py-2">{type.Name}</td>
        <td className="px-3 py-2 text-darkGrey/80">{type.Kind}</td>
        <td className="px-3 py-2 text-darkGrey/80">{type.SortOrder}</td>
        <td className="px-3 py-2 text-darkGrey/80">{type.IsActive ? 'Yes' : 'No'}</td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-3">
            <button type="button" onClick={startEdit} className="text-xs font-semibold text-navy hover:underline">Edit</button>
            <button type="button" onClick={() => mDelete.mutate()} disabled={mDelete.isPending} className="text-xs font-semibold text-red hover:underline disabled:opacity-40">Remove</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-border bg-bg/40">
      <td className="px-3 py-2 font-mono text-xs text-darkGrey/60">{type.Code}</td>
      <td className="px-3 py-2"><input aria-label={`Name for ${type.Code}`} value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} w-full`} /></td>
      <td className="px-3 py-2 text-darkGrey/60">{type.Kind}</td>
      <td className="px-3 py-2"><input aria-label={`Sort for ${type.Code}`} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} type="number" className={`${inputCls} w-20`} /></td>
      <td className="px-3 py-2">
        <label className="inline-flex items-center gap-1 text-xs font-semibold text-darkGrey">
          <input type="checkbox" aria-label={`Active for ${type.Code}`} checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
        </label>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => mUpdate.mutate()} disabled={!name.trim() || mUpdate.isPending} className="text-xs font-semibold text-navy hover:underline disabled:opacity-40">{mUpdate.isPending ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={() => setEditing(false)} className="text-xs font-semibold text-darkGrey/70 hover:underline">Cancel</button>
        </div>
      </td>
    </tr>
  );
}

function AssetFieldDefinitions() {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<string>('SIM');
  const [fieldKey, setFieldKey] = useState('');
  const [label, setLabel] = useState('');
  const [dataType, setDataType] = useState<FieldDataType>('Text');
  const [options, setOptions] = useState('');
  const [required, setRequired] = useState(false);

  const query = useQuery({ queryKey: ['asset-field-definitions', category], queryFn: ({ signal }) => listFieldDefinitions(category, signal) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['asset-field-definitions'] });

  const mCreate = useMutation({
    mutationFn: () => createFieldDefinition({
      Category: category,
      FieldKey: fieldKey.trim(),
      Label: label.trim(),
      DataType: dataType,
      Options: dataType === 'Select' ? options.split(',').map((o) => o.trim()).filter(Boolean) : null,
      IsRequired: required,
    }),
    onSuccess: () => { invalidate(); setFieldKey(''); setLabel(''); setOptions(''); setRequired(false); },
  });
  return (
    <div>
      <h2 className="mb-2 font-heading text-base font-semibold text-navy">Asset field definitions</h2>
      <p className="mb-2 text-xs text-darkGrey/70">Per-category custom fields stored in each asset's Attributes. Validated server-side on every write.</p>
      <label className="mb-2 inline-flex items-center gap-2 text-xs font-semibold text-darkGrey">
        Category
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
          {assetCategories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </label>
      <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr><th className="px-3 py-2">Key</th><th className="px-3 py-2">Label</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Required</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {query.data?.map((d) => (
              <DefinitionRow key={d.FieldDefinitionId} def={d} onChanged={invalidate} />
            ))}
            {query.data?.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-darkGrey/60">No fields for {category}.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <input aria-label="Field key" value={fieldKey} onChange={(e) => setFieldKey(e.target.value)} placeholder="FieldKey" className={inputCls} />
        <input aria-label="Field label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" className={inputCls} />
        <select aria-label="Field data type" value={dataType} onChange={(e) => setDataType(e.target.value as FieldDataType)} className={inputCls}>
          {fieldDataTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {dataType === 'Select' && <input aria-label="Field options" value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Options (comma-separated)" className={inputCls} />}
        <label className="inline-flex items-center gap-1 text-xs font-semibold text-darkGrey"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /> Required</label>
        <button type="button" disabled={!fieldKey.trim() || !label.trim() || mCreate.isPending} onClick={() => mCreate.mutate()} className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40">Add field</button>
      </div>
    </div>
  );
}

/**
 * One field-definition row with inline edit. Key + Category are immutable (they
 * form the stored JSON-key contract); Label, Type, Options and Required are
 * editable via PATCH — so e.g. a field can be made optional without deleting and
 * re-adding it.
 */
function DefinitionRow({ def, onChanged }: { def: AssetFieldDefinition; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(def.Label);
  const [dataType, setDataType] = useState<FieldDataType>(def.DataType);
  const [options, setOptions] = useState(def.Options?.join(', ') ?? '');
  const [required, setRequired] = useState(def.IsRequired);

  const startEdit = () => {
    setLabel(def.Label);
    setDataType(def.DataType);
    setOptions(def.Options?.join(', ') ?? '');
    setRequired(def.IsRequired);
    setEditing(true);
  };

  const mUpdate = useMutation({
    mutationFn: () => updateFieldDefinition(def.FieldDefinitionId, {
      Label: label.trim(),
      DataType: dataType,
      Options: dataType === 'Select' ? options.split(',').map((o) => o.trim()).filter(Boolean) : null,
      IsRequired: required,
    }),
    onSuccess: () => { setEditing(false); onChanged(); },
  });
  const mDelete = useMutation({ mutationFn: () => deleteFieldDefinition(def.FieldDefinitionId), onSuccess: onChanged });

  if (!editing) {
    return (
      <tr className="border-t border-border">
        <td className="px-3 py-2 font-mono text-xs">{def.FieldKey}</td>
        <td className="px-3 py-2">{def.Label}</td>
        <td className="px-3 py-2 text-darkGrey/80">{def.DataType}{def.Options ? ` (${def.Options.join(', ')})` : ''}</td>
        <td className="px-3 py-2 text-darkGrey/80">{def.IsRequired ? 'Yes' : 'No'}</td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-3">
            <button type="button" onClick={startEdit} className="text-xs font-semibold text-navy hover:underline">Edit</button>
            <button type="button" onClick={() => mDelete.mutate()} disabled={mDelete.isPending} className="text-xs font-semibold text-red hover:underline disabled:opacity-40">Remove</button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-border bg-bg/40">
      <td className="px-3 py-2 font-mono text-xs text-darkGrey/60">{def.FieldKey}</td>
      <td className="px-3 py-2"><input aria-label={`Label for ${def.FieldKey}`} value={label} onChange={(e) => setLabel(e.target.value)} className={`${inputCls} w-full`} /></td>
      <td className="px-3 py-2">
        <select aria-label={`Type for ${def.FieldKey}`} value={dataType} onChange={(e) => setDataType(e.target.value as FieldDataType)} className={inputCls}>
          {fieldDataTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {dataType === 'Select' && (
          <input aria-label={`Options for ${def.FieldKey}`} value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Options (comma-separated)" className={`${inputCls} mt-1 w-full`} />
        )}
      </td>
      <td className="px-3 py-2">
        <label className="inline-flex items-center gap-1 text-xs font-semibold text-darkGrey">
          <input type="checkbox" aria-label={`Required for ${def.FieldKey}`} checked={required} onChange={(e) => setRequired(e.target.checked)} /> Required
        </label>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => mUpdate.mutate()} disabled={!label.trim() || mUpdate.isPending} className="text-xs font-semibold text-navy hover:underline disabled:opacity-40">{mUpdate.isPending ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={() => setEditing(false)} className="text-xs font-semibold text-darkGrey/70 hover:underline">Cancel</button>
        </div>
      </td>
    </tr>
  );
}

const SYNC_JOBS = [
  { feature: 'AccountSync', label: 'Entra account sync', run: syncAccounts },
  { feature: 'IntuneSync', label: 'Intune device sync', run: syncIntune },
  { feature: 'ArcSync', label: 'Azure Arc server sync', run: syncArc },
  { feature: 'DefenderSync', label: 'Defender health sync (Intune)', run: syncDefender },
  { feature: 'CrowdStrikeSync', label: 'CrowdStrike check (Intune apps)', run: syncCrowdStrike },
  { feature: 'SystemAccountSync', label: 'System-account sync', run: syncSystemAccounts },
  { feature: 'DellWarrantySync', label: 'Dell warranty sync', run: syncDellWarranty },
] as const;

function SyncStatus() {
  return (
    <div>
      <h2 className="mb-2 font-heading text-base font-semibold text-navy">Sync jobs</h2>
      <div className="space-y-2">
        {SYNC_JOBS.map((j) => <SyncRow key={j.feature} feature={j.feature} label={j.label} run={j.run} />)}
        <SoftwareProvisioningRow />
        <OneDriveCopyRow />
      </div>
    </div>
  );
}

/**
 * Offboarding OneDrive-to-Vault copy processor (PR-33) — run the copy for all due
 * migrations now (Manual triggers immediately; AutoScan rows ≥ 7 days past
 * eligibility). Its own row (not a plain SyncRow) so it can show the detailed
 * result. The daily eligibility scan + copy timers run this automatically.
 */
function OneDriveCopyRow() {
  const copy = useMutation({ mutationFn: () => runOneDriveMigrationCopy() });
  return (
    <div className="rounded border border-border bg-white px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium text-navy">OneDrive → Vault migration</span>
        <button
          type="button"
          onClick={() => copy.mutate()}
          disabled={copy.isPending}
          className="shrink-0 rounded border border-border bg-white px-2 py-1 text-xs font-semibold text-navy hover:bg-bg disabled:opacity-40"
        >
          {copy.isPending ? 'Running…' : 'Run OneDrive copy now'}
        </button>
      </div>
      <p className="mt-1 text-xs text-darkGrey/70">
        Copies due leavers&rsquo; OneDrive content into the Vault archive library. Runs automatically daily; use this to force it now (e.g. after a manual trigger on a person). Requires <code>OSCAR_VAULT_SITE_URL</code> and the Graph permissions on <code>func-oscar</code>.
      </p>
      {copy.isError && (
        <p className="mt-1 text-sm text-red">{copy.error instanceof ApiError ? copy.error.message : 'Copy run failed.'}</p>
      )}
      {copy.isSuccess && (
        <p className="mt-1 text-sm text-green">
          {copy.data.RanSync
            ? `Processed ${copy.data.Processed} · copied ${copy.data.Copied} · skipped ${copy.data.Skipped} · failed ${copy.data.Failed}`
            : (copy.data.Note ?? 'Not configured — nothing run.')}
        </p>
      )}
    </div>
  );
}

function SyncRow({ feature, label, run }: { feature: string; label: string; run: () => Promise<unknown> }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['audit', feature], queryFn: ({ signal }) => getAudit({ feature, take: 1 }, signal) });
  const latest = query.data?.[0];
  const mRun = useMutation({ mutationFn: () => run(), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['audit', feature] }) });

  return (
    <div className="flex items-center justify-between rounded border border-border bg-white px-3 py-2 text-sm">
      <div>
        <span className="font-medium text-navy">{label}</span>
        <span className="text-darkGrey/70"> · last run {latest ? formatDateTime(latest.ChangedDate) : 'never'}</span>
      </div>
      <button type="button" disabled={mRun.isPending} onClick={() => mRun.mutate()} className="rounded border border-border bg-white px-2 py-1 text-xs font-semibold text-navy hover:bg-bg disabled:opacity-40">
        {mRun.isPending ? 'Running…' : 'Run now'}
      </button>
    </div>
  );
}

/**
 * Software provisioning — force the mandatory-role-software reconcile now.
 * Sits in the Sync jobs list so all "run now" controls live together. Unlike a
 * plain SyncRow it shows a detailed result (seats provisioned / people affected
 * / reclaim counts), so it renders as its own row rather than reusing SyncRow.
 */
function SoftwareProvisioningRow() {
  const provision = useMutation({ mutationFn: () => runSoftwareProvisionSweep() });
  return (
    <div className="rounded border border-border bg-white px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium text-navy">Software provisioning</span>
        <button
          type="button"
          onClick={() => provision.mutate()}
          disabled={provision.isPending}
          className="shrink-0 rounded border border-border bg-white px-2 py-1 text-xs font-semibold text-navy hover:bg-bg disabled:opacity-40"
        >
          {provision.isPending ? 'Provisioning…' : 'Run software provisioning now'}
        </button>
      </div>
      <p className="mt-1 text-xs text-darkGrey/70">
        Reconciles mandatory role software for everyone from their current department. Runs automatically every hour and when a person&rsquo;s department changes — use this to force it now (e.g. after editing the Software Allocation board). Auto-reclaim of unentitled seats is applied only when <code>SOFTWARE_AUTO_REVOKE_ENABLED</code> is on; otherwise it reports what it <em>would</em> revoke.
      </p>
      {provision.isError && (
        <p className="mt-1 text-sm text-red">{provision.error instanceof ApiError ? provision.error.message : 'Provisioning failed.'}</p>
      )}
      {provision.isSuccess && (
        <p className="mt-1 text-sm text-green">
          {provision.data.Created} seat(s) provisioned · {provision.data.PeopleAffected} person(s) affected
        </p>
      )}
      {provision.isSuccess && (provision.data.Reclaimed > 0 || provision.data.ReclaimPreview > 0) && (
        <p className="mt-1 text-xs text-darkGrey/70">
          {provision.data.ReclaimEnabled
            ? `Auto-reclaim: ${provision.data.Reclaimed} unentitled seat(s) revoked.`
            : `Auto-reclaim preview: ${provision.data.ReclaimPreview} unentitled seat(s) would be revoked — set SOFTWARE_AUTO_REVOKE_ENABLED=true to apply.`}
        </p>
      )}
    </div>
  );
}

function InfoCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded border border-border bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-navy">{title}</h3>
      <p className="mt-1 text-xs text-darkGrey/80">{body}</p>
    </div>
  );
}
